// utils/clientEnv.js

export async function getPublicIP() {
  // Intento 1: ipify (JSON)
  try {
    const r = await fetch('https://api.ipify.org?format=json', {
      cache: 'no-store',
    });
    if (r.ok) {
      const { ip } = await r.json();
      if (ip) return ip.trim();
    }
  } catch (error) {
    console.log(error);
  }

  // Intento 2: icanhazip (texto)
  try {
    const r = await fetch('https://icanhazip.com', {
      cache: 'no-store',
    });
    if (r.ok) {
      const text = (await r.text()).trim();
      if (text) return text;
    }
  } catch (error) {
    console.log(error);
  }

  // Intento 3: Cloudflare trace (texto con pares clave=valor)
  try {
    const r = await fetch(
      'https://www.cloudflare.com/cdn-cgi/trace',
      {
        cache: 'no-store',
      }
    );
    if (r.ok) {
      const t = await r.text();
      const line = t.split('\n').find((l) => l.startsWith('ip='));
      if (line) return line.split('=')[1].trim();
    }
  } catch (error) {
    console.log(error);
  }

  return null; // No se pudo obtener
}

// Detecta navegador, versión, SO y si es móvil
export function getBrowserInfo() {
  const hasNavigator = typeof navigator !== 'undefined';
  const uaStr =
    hasNavigator && navigator.userAgent ? navigator.userAgent : '';

  const info = {
    browserName: null,
    browserVersion: null,
    os: null,
    mobile: false,
    ua: uaStr || null,
  };

  if (!hasNavigator) return info; // SSR/Node: devolvemos lo que tenemos

  // 1) User-Agent Client Hints (cuando existe)
  const uaData =
    navigator.userAgentData || navigator.userAgent?.data || null;
  if (uaData?.brands?.length) {
    // Suele venir algo como [{brand:"Chromium",version:"120"}, {brand:"Google Chrome",version:"120"}]
    const brand =
      uaData.brands.find((b) => !/Not.?A.?Brand/i.test(b.brand)) ||
      uaData.brands[0];
    info.browserName = brand?.brand || null;
    info.browserVersion = brand?.version || null;
    info.mobile = !!uaData.mobile;
    // os no viene siempre; inferimos con UA clásico si hace falta
  }

  // 2) Fallback al UA clásico (regex robusto)
  if (!info.browserName || !info.browserVersion || !info.os) {
    const ua = uaStr || '';
    // Orden importa para diferenciar Edge/Chrome/Chromium
    const rules = [
      { name: 'Edge', re: /Edg\/([\d.]+)/ },
      { name: 'Chrome', re: /Chrome\/([\d.]+)/ },
      { name: 'Firefox', re: /Firefox\/([\d.]+)/ },
      { name: 'Safari', re: /Version\/([\d.]+).*Safari/ },
      { name: 'Opera', re: /OPR\/([\d.]+)/ },
      { name: 'IE', re: /MSIE\s([\d.]+)|Trident.*rv:([\d.]+)/ },
    ];
    for (const r of rules) {
      const m = ua.match(r.re);
      if (m) {
        info.browserName = info.browserName || r.name;
        info.browserVersion =
          info.browserVersion || m[1] || m[2] || null;
        break;
      }
    }

    // SO
    if (/Windows NT 10\.0/.test(ua)) info.os = 'Windows 10/11';
    else if (/Windows NT 6\.3/.test(ua)) info.os = 'Windows 8.1';
    else if (/Windows NT 6\.2/.test(ua)) info.os = 'Windows 8';
    else if (/Windows NT 6\.1/.test(ua)) info.os = 'Windows 7';
    else if (/Mac OS X 10[._]\d+/.test(ua) || /Macintosh/.test(ua))
      info.os = 'macOS';
    else if (/Android/.test(ua)) info.os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) info.os = 'iOS/iPadOS';
    else if (/Linux/.test(ua)) info.os = 'Linux';

    info.mobile =
      info.mobile || /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  }

  return info;
}

/* ======== NUEVO: utilidades para formatear el label del navegador ======== */

function mapBrowserName(name) {
  if (!name) return null;
  const n = name.toLowerCase();
  if (
    n.includes('google chrome') ||
    n.includes('chromium') ||
    n === 'chrome'
  )
    return 'Chrome';
  if (n.includes('edge') || n === 'edg') return 'Microsoft Edge';
  if (n.includes('firefox')) return 'Firefox';
  if (n.includes('safari')) return 'Safari';
  if (n.includes('opera') || n.includes('opr')) return 'Opera';
  return name; // fallback sin tocar
}

// Intenta obtener versión completa y bitness con UA-CH (cuando existe)
async function getHighEntropy() {
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.userAgentData?.getHighEntropyValues
    ) {
      const he = await navigator.userAgentData.getHighEntropyValues([
        'uaFullVersion',
        'bitness',
      ]);
      return {
        uaFullVersion: he?.uaFullVersion || null,
        bitness: he?.bitness || null, // "64" o "32" en la práctica
      };
    }
  } catch (error) {
    console.warn(
      'Unable to read userAgentData high entropy values',
      error
    );
  }
  return { uaFullVersion: null, bitness: null };
}

// Deriva 32/64 bits desde el UA clásico si hace falta
function inferBitsFromUA(ua = '') {
  if (
    /\bWOW64\b|\bWin64\b|\bx86_64\b|\bx64\b|\bamd64\b|\barm64\b/i.test(
      ua
    )
  )
    return '64';
  if (/\bWin32\b|\bx86\b/i.test(ua)) return '32';
  return null;
}

// Construye el texto final: "Chrome versión X (Build oficial)(64 bits)"
export async function formatBrowserLabel(baseInfo) {
  const info = baseInfo || getBrowserInfo();

  const { uaFullVersion, bitness } = await getHighEntropy();
  const name = mapBrowserName(info.browserName) || 'Navegador';
  const version = uaFullVersion || info.browserVersion || '';
  const bits = bitness || inferBitsFromUA(info.ua) || '';

  const build = 'Build oficial';
  const bitsText = bits ? `(${bits} bits)` : '';

  return `${name} versión ${version} (${build})${bitsText}`;
}

// Helper completo que devuelve todo junto, incluyendo browserLabel
export async function getClientEnv() {
  const [ip, browser] = await Promise.all([
    getPublicIP(),
    Promise.resolve(getBrowserInfo()),
  ]);
  const browserLabel = await formatBrowserLabel(browser);
  return { ip, ...browser, browserLabel };
}
